import React from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, alpha, Tooltip,
} from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { ColumnFilter } from '../filters/ColumnFilter';
import { SEARCE_COLORS } from '../../theme';
import type { AssetResponse, ColumnFilterMenuState } from '../../types/assets';
import { ASSET_TABLE_COLUMNS, ASSET_TABLE_TOTAL_WIDTH } from '../../types/assets';
import { formatDate, trimText } from '../../utils/assetHelpers';

interface AssetTableProps {
  paginatedAssets: AssetResponse[];
  totalFilteredCount: number;
  emptyMessage: string;
  filterMenus: Record<string, ColumnFilterMenuState>;
  getUniqueValuesForColumn: (columnId: string) => string[];
  onFilterClick: (columnId: string, event: React.MouseEvent<HTMLElement>) => void;
  onFilterClose: (columnId: string) => void;
  onFilterSelect: (columnId: string, selectedValues: string[]) => void;
  onRequestSort: (columnId: string) => void;
  onAssetClick: (asset: AssetResponse) => void;
}

const AssetTable: React.FC<AssetTableProps> = ({
  paginatedAssets,
  totalFilteredCount,
  emptyMessage,
  filterMenus,
  getUniqueValuesForColumn,
  onFilterClick,
  onFilterClose,
  onFilterSelect,
  onRequestSort,
  onAssetClick,
}) => (
  <TableContainer
    component={Paper}
    variant="outlined"
    sx={{ maxHeight: 'min(70vh, 600px)', overflow: 'auto' }}
  >
    <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: ASSET_TABLE_TOTAL_WIDTH }}>
      <TableHead>
        <TableRow>
          {ASSET_TABLE_COLUMNS.map(col => (
            <TableCell
              key={col.id}
              sx={{
                fontWeight: 600, fontSize: '0.7rem', color: SEARCE_COLORS.white,
                backgroundColor: SEARCE_COLORS.primary,
                width: col.width,
                minWidth: col.width,
                zIndex: 2, position: 'sticky', top: 0, borderBottom: '1px solid #e0e0e0',
                '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.9) },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onRequestSort(col.id);
                  }}
                  aria-label={`Sort by ${col.label}`}
                  sx={{
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    p: 0,
                    m: 0,
                    textAlign: 'left',
                    flex: 1,
                    minWidth: 0,
                    color: SEARCE_COLORS.white,
                    '&:hover': { opacity: 0.92 },
                    '&:focus-visible': {
                      outline: `2px solid ${alpha(SEARCE_COLORS.white, 0.55)}`,
                      outlineOffset: 2,
                      borderRadius: 0.5,
                    },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      color: 'inherit',
                    }}
                  >
                    {col.label}
                  </Typography>
                </Box>
                {col.filterable && (
                  <IconButton
                    size="small"
                    onClick={e => onFilterClick(col.id, e)}
                    aria-label={`Filter ${col.label}`}
                    sx={{
                      flexShrink: 0,
                      color: (filterMenus[col.id]?.selectedValues?.length ?? 0) > 0
                        ? SEARCE_COLORS.warning
                        : SEARCE_COLORS.white,
                      '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.15) },
                      padding: '2px',
                    }}
                  >
                    <FilterListIcon fontSize="small" sx={{ fontSize: '0.55rem' }} />
                  </IconButton>
                )}
              </Box>
              {col.filterable && (
                <ColumnFilter
                  anchorEl={filterMenus[col.id]?.anchorEl ?? null}
                  open={Boolean(filterMenus[col.id]?.anchorEl)}
                  onClose={() => onFilterClose(col.id)}
                  onSelect={values => onFilterSelect(col.id, values)}
                  options={getUniqueValuesForColumn(col.id)}
                  selectedValues={filterMenus[col.id]?.selectedValues ?? []}
                  title={col.label}
                  enableSearch
                />
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {totalFilteredCount === 0 ? (
          <TableRow>
            <TableCell
              colSpan={ASSET_TABLE_COLUMNS.length}
              align="center"
              sx={{ py: 6, color: 'text.secondary', fontSize: '0.775rem' }}
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          paginatedAssets.map(row => (
            <TableRow key={row.id} hover onClick={() => onAssetClick(row)} sx={{ cursor: 'pointer' }}>
              <TableCell>
                <Typography variant="body2" noWrap fontWeight={600} sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.775rem' }}>
                  {row.asset_code ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={row.asset_name}>
                  <Typography variant="body2" noWrap fontWeight={500} sx={{ fontSize: '0.775rem' }}>
                    {trimText(row.asset_name, 30)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.775rem' }}>
                  {row.asset_type || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.775rem' }}>
                  {row.gtm_category || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={row.key_cloud_services.join(', ')} arrow placement="top">
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
                    {row.key_cloud_services.slice(0, 2).map((s, i) => (
                      <Chip key={i} label={s} size="small" sx={{ fontSize: '0.55rem', height: 22, flexShrink: 0 }} />
                    ))}
                    {row.key_cloud_services.length > 2 && (
                      <Chip
                        label={`+${row.key_cloud_services.length - 2}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.5rem', height: 22, flexShrink: 0 }}
                      />
                    )}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  label={row.marketplace_listing_status ? 'Yes' : 'No'}
                  size="small"
                  color={row.marketplace_listing_status ? 'success' : 'default'}
                  sx={{ fontSize: '0.55rem', height: 22 }}
                />
              </TableCell>
              <TableCell>
                <Tooltip title={row.submitter_business_unit || ''}>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.775rem' }}>
                    {row.submitter_business_unit?.trim() || '—'}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={row.submitter_sub_bu || ''}>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.775rem' }}>
                    {row.submitter_sub_bu?.trim() || '—'}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.775rem' }}>
                  {trimText(row.submitted_by, 25)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.775rem' }}>
                  {formatDate(row.submitted_at)}
                </Typography>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

export default AssetTable;
